/**
 * P2P-Q — Signed Handshake + Protocol/Feature Negotiation
 *
 * Peers exchange a signed handshake immediately on DataChannel open (and on
 * first Gun/direct exchange) so they can negotiate capabilities before trusting
 * any P2P payload.
 *
 * REQ-P2P-14 / REQ-P2P-15
 */

import { compareVersions } from './semver-compare';
import { isWellFormedCredential, type BuildTrustLabel, type OfficialBuildCredential } from './official-build-credential';
import type { SecureSessionOffer } from './p2p-secure-session';

export const APP_NAME = 'iinpublic';
export const APP_VERSION = '1.0.0';

/** `readNativeHostInfo().version`'s literal 'web' fallback (outside any packaged build) and any
 * other non-numeric string aren't a real, comparable version — treat as 'unknown', not 'same'. */
function isComparableVersion(version: string | null | undefined): version is string {
  return typeof version === 'string' && /^\d+(\.\d+)*$/.test(version.trim());
}

/** Wire protocols ordered newest-first; the highest mutually-supported version wins. */
export const SUPPORTED_PROTOCOLS = ['iinpublic-p2p-v2'] as const;
export type SupportedProtocol = (typeof SUPPORTED_PROTOCOLS)[number];

/** Optional capability flags advertised during handshake. */
export type HandshakeFeature =
  | 'signed-discovery'
  | 'encrypted-signaling'
  | 'webrtc-datachannel'
  | 'relay-fallback'
  | 'pair-private-responses'
  | 'ledger-sync'
  | 'schema-migrations'
  | 'encrypted-session-v1';

/** Handshake payload sent over the DataChannel immediately after open. */
export type P2PHandshakePayload = {
  appName: string;
  appVersion: string;
  supportedProtocols: SupportedProtocol[];
  features: HandshakeFeature[];
  /** SHA-256(pub) derived peer id. */
  peerId: string;
  /** Stable account id expected by the connection initiator. */
  userId: string;
  /** SEA public key (no private material). */
  publicKey: string;
  /** Fresh ECDH offer; its signed transcript derives the per-connection AES key. */
  secureSession: SecureSessionOffer;
  timestamp: string;
  /** Scenario 2 (§16): this device's current OfficialBuildCredential, if it has one (native
   * attestation bridge available + a successful verify round trip — see
   * web-attestation-service.ts). Small enough to send inline, no extra round trip at connect
   * time. Absent on any platform without the bridge, or if attestation hasn't completed yet —
   * both correctly evaluate to 'unverified-build' on the receiving side, never an error. */
  buildTrustCredential?: OfficialBuildCredential;
};

/** Wire frame type for the handshake exchange. */
export type HandshakeFrame = {
  type: 'handshake';
  payload: P2PHandshakePayload;
};

export type ProtocolNegotiationResult =
  | { ok: true; selectedProtocol: SupportedProtocol; unsupportedFeatures: HandshakeFeature[] }
  | { ok: false; reason: string };

/**
 * `local-older`/`local-newer` compares this device's appVersion against the remote peer's —
 * the direction a caller needs to decide which side to remind (upgrade vs. feature caution).
 * `unknown` when either version is missing/unparseable rather than guessing `same`.
 */
export type VersionMismatchDirection = 'local-older' | 'local-newer' | 'same' | 'unknown';

export type HandshakeDiagnostics = {
  localAppVersion: string;
  remoteAppVersion: string | null;
  selectedProtocol: SupportedProtocol | null;
  unsupportedFeatures: HandshakeFeature[];
  handshakeState: 'pending' | 'ok' | 'failed';
  failureReason: string | null;
  versionMismatch: VersionMismatchDirection;
  /** Scenario 2 trust label for the remote peer's build. `buildHandshakeDiagnostics` itself
   * always sets this to 'unverified-build' (the correct default absent any evaluation, since
   * this module is deliberately protocol-only and has no verifier-key/official-identifier
   * context) — the session layer (p2p-webrtc-session.ts), which does have that context, always
   * overwrites it with the real evaluateBuildTrust() result right after calling this function.
   * Never left stale: see maybeEvaluateBuildTrust in p2p-webrtc-session.ts. */
  buildTrust: BuildTrustLabel;
};

/**
 * Build the handshake payload to send to the remote peer.
 */
export function buildHandshakePayload(params: {
  peerId: string;
  userId: string;
  publicKey: string;
  secureSession: SecureSessionOffer;
  appVersion?: string;
  features?: HandshakeFeature[];
  buildTrustCredential?: OfficialBuildCredential;
  now?: Date;
}): P2PHandshakePayload {
  if (!params.peerId) throw new Error('handshake requires peerId');
  if (!params.userId) throw new Error('handshake requires userId');
  if (!params.publicKey) throw new Error('handshake requires publicKey');
  return {
    appName: APP_NAME,
    appVersion: params.appVersion ?? APP_VERSION,
    supportedProtocols: [...SUPPORTED_PROTOCOLS],
    features: params.features ?? [
      'signed-discovery',
      'encrypted-signaling',
      'webrtc-datachannel',
      'relay-fallback',
      'pair-private-responses',
      'ledger-sync',
      'schema-migrations',
      'encrypted-session-v1',
    ],
    peerId: params.peerId,
    userId: params.userId,
    publicKey: params.publicKey,
    secureSession: params.secureSession,
    timestamp: (params.now ?? new Date()).toISOString(),
    ...(params.buildTrustCredential ? { buildTrustCredential: params.buildTrustCredential } : {}),
  };
}

/**
 * Negotiate the highest common protocol from two peers' handshakes.
 * If either peer's list is empty or there is no overlap, returns { ok: false }.
 * Unknown features in the remote list are silently ignored.
 */
export function negotiateProtocol(
  local: P2PHandshakePayload,
  remote: P2PHandshakePayload,
): ProtocolNegotiationResult {
  if (!local.supportedProtocols?.length || !remote.supportedProtocols?.length) {
    return { ok: false, reason: 'empty protocol list' };
  }
  // Prefer protocols advertised by local (ordered newest-first) that also appear in remote.
  const remoteSet = new Set<string>(remote.supportedProtocols);
  const selected = local.supportedProtocols.find((p) => remoteSet.has(p));
  if (!selected) {
    return {
      ok: false,
      reason: `no common protocol: local=${local.supportedProtocols.join(',')}, remote=${remote.supportedProtocols.join(',')}`,
    };
  }
  const remoteFeatures = new Set<string>(remote.features ?? []);
  const unsupportedFeatures = (local.features ?? []).filter((f) => !remoteFeatures.has(f));
  return { ok: true, selectedProtocol: selected, unsupportedFeatures };
}

/**
 * Validate that a received handshake payload has the required fields and a
 * timestamp within the allowed skew window.  Does NOT verify the SEA signature
 * (that is done separately via `verifySignedP2PEnvelopeProof`).
 */
export function validateHandshakePayload(
  payload: unknown,
  opts: { now?: Date; maxSkewMs?: number } = {},
): { ok: true; payload: P2PHandshakePayload } | { ok: false; reason: string } {
  if (!payload || typeof payload !== 'object') {
    return { ok: false, reason: 'handshake payload is not an object' };
  }
  const p = payload as Record<string, unknown>;
  if (typeof p.appName !== 'string' || !p.appName) {
    return { ok: false, reason: 'missing appName' };
  }
  if (typeof p.appVersion !== 'string' || !p.appVersion) {
    return { ok: false, reason: 'missing appVersion' };
  }
  if (!Array.isArray(p.supportedProtocols) || p.supportedProtocols.length === 0) {
    return { ok: false, reason: 'missing or empty supportedProtocols' };
  }
  if (!Array.isArray(p.features)) {
    return { ok: false, reason: 'missing features list' };
  }
  if (typeof p.peerId !== 'string' || !p.peerId) {
    return { ok: false, reason: 'missing peerId' };
  }
  if (typeof p.userId !== 'string' || !p.userId) {
    return { ok: false, reason: 'missing userId' };
  }
  if (typeof p.publicKey !== 'string' || !p.publicKey) {
    return { ok: false, reason: 'missing publicKey' };
  }
  if (!p.secureSession || typeof p.secureSession !== 'object') {
    return { ok: false, reason: 'missing secureSession offer' };
  }
  const secure = p.secureSession as Record<string, unknown>;
  if (
    secure.version !== 1 ||
    typeof secure.ephemeralPublicKey !== 'string' ||
    secure.ephemeralPublicKey.length < 80 ||
    secure.ephemeralPublicKey.length > 120 ||
    typeof secure.sessionNonce !== 'string' ||
    secure.sessionNonce.length < 20 ||
    secure.sessionNonce.length > 64
  ) {
    return { ok: false, reason: 'malformed secureSession offer' };
  }
  if (!p.supportedProtocols.includes('iinpublic-p2p-v2')) {
    return { ok: false, reason: 'encrypted protocol required' };
  }
  if (!p.features.includes('encrypted-session-v1')) {
    return { ok: false, reason: 'encrypted-session-v1 feature required' };
  }
  if (typeof p.timestamp !== 'string' || !p.timestamp) {
    return { ok: false, reason: 'missing timestamp' };
  }
  const created = new Date(p.timestamp as string).getTime();
  if (!Number.isFinite(created)) {
    return { ok: false, reason: 'invalid timestamp' };
  }
  const now = (opts.now ?? new Date()).getTime();
  const maxSkewMs = opts.maxSkewMs ?? 120_000;
  if (Math.abs(now - created) > maxSkewMs) {
    return { ok: false, reason: 'stale handshake timestamp' };
  }
  // Optional — absent is fine (no attestation bridge, or not completed yet). Present-but-
  // malformed is dropped rather than silently ignored: a peer sending a broken credential is
  // either buggy or tampering, and evaluateBuildTrust would reject it anyway, so failing the
  // whole handshake validation here is no worse and catches it earlier.
  if (p.buildTrustCredential !== undefined && !isWellFormedCredential(p.buildTrustCredential)) {
    return { ok: false, reason: 'malformed buildTrustCredential' };
  }
  return { ok: true, payload: p as unknown as P2PHandshakePayload };
}

/**
 * Build a diagnostics snapshot from local and remote handshake payloads.
 */
export function buildHandshakeDiagnostics(
  local: P2PHandshakePayload,
  remote: P2PHandshakePayload | null,
  negotiationResult: ProtocolNegotiationResult | null,
): HandshakeDiagnostics {
  const state: HandshakeDiagnostics['handshakeState'] = !remote
    ? 'pending'
    : !negotiationResult
      ? 'pending'
      : negotiationResult.ok
        ? 'ok'
        : 'failed';
  const versionMismatch: VersionMismatchDirection =
    !isComparableVersion(local.appVersion) || !isComparableVersion(remote?.appVersion)
      ? 'unknown'
      : (() => {
          const cmp = compareVersions(local.appVersion, remote!.appVersion);
          if (cmp === 0) return 'same';
          return cmp > 0 ? 'local-newer' : 'local-older';
        })();

  return {
    localAppVersion: local.appVersion,
    remoteAppVersion: remote?.appVersion ?? null,
    selectedProtocol:
      negotiationResult?.ok ? negotiationResult.selectedProtocol : null,
    unsupportedFeatures:
      negotiationResult?.ok ? negotiationResult.unsupportedFeatures : [],
    handshakeState: state,
    failureReason:
      negotiationResult && !negotiationResult.ok ? negotiationResult.reason : null,
    versionMismatch,
    buildTrust: 'unverified-build',
  };
}
